import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ToastItemData,
  ToastListContainer,
  ToastPosition,
  ToastVariant,
} from 'Components/Display/Toast';
import {
  FlexWrapper,
  LoadingIndicator,
} from 'Components/Display';
import { ToastType } from 'Models/SharedModels';

// ── Public context shape ──────────────────────────────────────────────────

interface ToastAddOptions {
  autoClose?: number | false;
  type?: ToastVariant;
  position?: ToastPosition;
}

interface ToastContextValue {
  dismiss: (toastId: string) => void;
  add: (content: any, options?: ToastAddOptions) => string;
  error: (content: any) => void;
  errors: (content: string[]) => void;
  success: (content: any) => void;
  info: (content: any) => void;
  warning: (content: any) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

// ── Provider ─────────────────────────────────────────────────────────────

interface ToastProviderProps {
  position?: ToastPosition;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
}) => {
  const [toasts, setToasts] = useState<ToastItemData[]>([]);
  const timers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const add = useCallback(
    (content: any, options: ToastAddOptions = {}): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const autoClose = options.autoClose !== undefined ? options.autoClose : 5000;
      const variant: ToastVariant = options.type ?? 'generic';

      setToasts((prev) => [...prev, { id, content, variant, autoClose, position: options.position }]);

      if (autoClose !== false) {
        const timer = setTimeout(() => dismiss(id), autoClose as number);
        timers.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  const error = useCallback(
    (content: any) => add(content, { type: 'error' }),
    [add]
  );

  const errors = useCallback(
    (content: string[]) => content.forEach((msg) => add(msg, { type: 'error' })),
    [add]
  );

  const success = useCallback(
    (content: any) => add(content, { type: 'success' }),
    [add]
  );

  const info = useCallback(
    (content: any) => add(content, { type: 'info' }),
    [add]
  );

  const warning = useCallback(
    (content: any) => add(content, { type: 'warning' }),
    [add]
  );

  const value: ToastContextValue = { dismiss, add, error, errors, success, info, warning };

  const toastsByPosition = toasts.reduce<Record<string, ToastItemData[]>>(
    (acc, t) => {
      const pos = t.position ?? position;
      if (!acc[pos]) acc[pos] = [];
      acc[pos].push(t);
      return acc;
    },
    {}
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {ReactDOM.createPortal(
        <>
          {Object.entries(toastsByPosition).map(([pos, items]) => (
            <ToastListContainer
              key={pos}
              items={items}
              position={pos as ToastPosition}
              onDismiss={dismiss}
            />
          ))}
        </>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

// ── Hooks ─────────────────────────────────────────────────────────────────

export const useToasts = (): ToastContextValue => {
  const ctx = React.useContext(ToastContext);
  if (ctx === undefined) {
    throw new Error('useToasts must be used within a ToastProvider');
  }
  return ctx;
};

export function useLoadingToast() {
  const toast = useToasts();
  const loadingToastId = useRef<string | null>(null);

  const showToast = (customText?: string) => {
    loadingToastId.current = toast.add(
      <FlexWrapper className="flex-vertical-center">
        <LoadingIndicator indicatorSize={20} noLabel />
        <span className="ml-2">{customText || 'Loading - Please Wait...'}</span>
      </FlexWrapper>,
      { autoClose: false, type: 'info' }
    );
  };

  const hideToast = () => {
    if (loadingToastId.current !== null) {
      toast.dismiss(loadingToastId.current);
      loadingToastId.current = null;
    }
  };

  return [showToast, hideToast] as const;
}

export function useDownloadingToast() {
  const toast = useToasts();
  const loadingToastId = useRef<string | null>(null);

  const showToast = (customText?: string) => {
    loadingToastId.current = toast.add(
      <FlexWrapper className="flex-vertical-center">
        <LoadingIndicator indicatorSize={20} noLabel />
        <span className="ml-2">{customText || 'Downloading File - Please Wait...'}</span>
      </FlexWrapper>,
      { autoClose: false, type: 'info' }
    );
  };

  const hideToast = () => {
    if (loadingToastId.current !== null) {
      toast.dismiss(loadingToastId.current);
      loadingToastId.current = null;
    }
  };

  return [showToast, hideToast] as const;
}

// ── Backward-compat re-exports ────────────────────────────────────────────

export type FormattedToastProps = {
  type: ToastType;
  content?: any;
  hideIcon?: boolean;
};

export const FormattedToast: React.FC<FormattedToastProps> = ({ content }) => (
  <div>{content}</div>
);

export const ToastIcon: React.FC<FormattedToastProps> = () => null;
